

const firstTruthy = (...readers) => readers.reduce((result, read) => result || read(), undefined);
const everyTruthy = (...readers) => readers.reduce((result, read) => result && read(), true);const INLINE_HEADING_RE = /(^|[^\n])\s+(#{1,6})\s+/g;
const INLINE_HR_RE = /(^|[^\n])\s+---\s+/g;
const INLINE_LIST_RE = /(?<=[。；;：:])\s+([-*+])\s+/g;
const HEADING_RE = /^(#{1,6})\s+(.+?)\s*$/;
const CJK_RE = /[\u4e00-\u9fff]/;
const SENTENCE_RE = /[。.!！?？；;：:]/;
const COMPACT_TABLE_BOUNDARY_RE = /\s*\|\|\s*/g;
const TABLE_SEPARATOR_CELL_RE = /^:?-{3,}:?$/;
const TABLE_TITLE_PUNCTUATION_RE = /[。.!！?？；;：:]/;

function splitInlineMarkers(markdown = '') {
  return String(markdown || '').split('\n').map((line) => {
    const stripped = String(line || '').trim();
    if (stripped.startsWith('|') && stripped.endsWith('|')) {
      return line;
    }
    return String(line || '')
      .replace(INLINE_HR_RE, '$1\n\n---\n\n')
      .replace(INLINE_HEADING_RE, '$1\n\n$2 ')
      .replace(INLINE_LIST_RE, '\n$1 ');
  }).join('\n');
}

function pipeCells(line = '') {
  return String(line || '').trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((cell) => cell.trim());
}

function isTableSeparatorRow(line = '') {
  const cells = pipeCells(line).filter(Boolean);
  return cells.length >= 2 && cells.every((cell) => TABLE_SEPARATOR_CELL_RE.test(cell));
}

function formatPipeRow(cells = []) {
  return `| ${cells.map((cell) => String(cell || '').trim()).join(' | ')} |`;
}

function looksLikeLeadingTableTitle(value = '') {
  const text = String(value || '').trim();
  const titleText = text.replace(/^[0-9]+(?:[.．][0-9]+)*[、.．]?\s*/, '');
  return Boolean(text) && text.length <= 48 && !TABLE_TITLE_PUNCTUATION_RE.test(titleText);
}

function normalizeCompactTableLines(lines = []) {
  const result = [];
  const hasSeparator = lines.some((line) => isTableSeparatorRow(String(line || '').trim()));
  lines.forEach((line, index) => {
    const stripped = String(firstTruthy(() => (line), () => (''))).trim();
    if (!stripped) {
      return;
    }
    const nextLine = String(firstTruthy(() => (lines[index + 1]), () => (''))).trim();
    const nextIsSeparator = isTableSeparatorRow(nextLine);
    const currentIsSeparator = isTableSeparatorRow(stripped);
    const previousIsSeparator = everyTruthy(() => (result.length > 0), () => (isTableSeparatorRow(result[result.length - 1])));
    if (!stripped.includes('|')) {
      result.push(String(firstTruthy(() => (line), () => (''))).trimEnd());
      return;
    }
    const cells = pipeCells(stripped);
    if (nextIsSeparator) {
      const separatorColumns = pipeCells(nextLine).length;
      if (everyTruthy(() => (cells.length === separatorColumns + 1), () => (looksLikeLeadingTableTitle(cells[0])))) {
        result.push(cells[0]);
        result.push(formatPipeRow(cells.slice(1)));
        return;
      }
    }
    if (firstTruthy(() => (currentIsSeparator), () => (nextIsSeparator), () => (previousIsSeparator), () => (hasSeparator))) {
      result.push(formatPipeRow(cells));
      return;
    }
    result.push(String(firstTruthy(() => (line), () => (''))).trimEnd());
  });
  return result;
}

function splitCompactTables(markdown = '') {
  const lines = [];
  String(markdown || '').split('\n').forEach((line) => {
    if (!String(line || '').includes('||')) {
      lines.push(line);
      return;
    }
    const compactLines = String(line || '').trim().replace(COMPACT_TABLE_BOUNDARY_RE, '|\n|').split('\n');
    if (!compactLines.some((item) => isTableSeparatorRow(String(item || '').trim()))) {
      lines.push(line);
      return;
    }
    lines.push(...normalizeCompactTableLines(compactLines));
  });
  return lines.join('\n');
}

function headingParts(line = '') {
  const matched = String(firstTruthy(() => (line), () => (''))).trim().match(HEADING_RE);
  if (!matched) {
    return null;
  }
  const body = String(firstTruthy(() => (matched[2]), () => (''))).trim();
  const separatorIndex = body.indexOf(' ');
  if (separatorIndex <= 0) {
    return null;
  }
  const title = body.slice(0, separatorIndex).trim();
  const rest = body.slice(separatorIndex + 1).trim();
  if (firstTruthy(() => (title.length > 24), () => (rest.length < 8))) {
    return null;
  }
  if (firstTruthy(() => (!CJK_RE.test(title)), () => (SENTENCE_RE.test(title)))) {
    return null;
  }
  return { marker: matched[1], title, rest };
}

function splitHeadingLine(line = '') {
  const parts = headingParts(line);
  if (!parts) {
    return [String(line || '').trimEnd()];
  }
  return [`${parts.marker} ${parts.title}`, '', parts.rest];
}

export function normalizeAnalysisMarkdown(markdown = '') {
  const text = String(markdown || '').replace(/\r\n/g, '\n').trim();
  if (!text) {
    return '';
  }
  const lines = [];
  splitInlineMarkers(splitCompactTables(text)).split('\n').forEach((line) => {
    lines.push(...splitHeadingLine(line));
  });
  return lines.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}
